import { CSSProperties, HTMLAttributes } from 'react';

import scssVariables from '@styles/variables.module.scss';
import cx from 'classnames';

import styles from './Icon.module.scss';

export type IconColor =
  | 'error'
  | 'success'
  | 'warning'
  | 'info'
  | 'secondary'
  | 'lightGray'
  | 'currentColor'
  | string;

export type IconSize =
  | 'micro'
  | 'tiny'
  | 'small'
  | 'medium'
  | 'large'
  | 'xLarge'
  | 'xxLarge'
  | 'xxxLarge'
  | number;

export interface IconProps extends HTMLAttributes<HTMLSpanElement> {
  name: string;
  size?: IconSize;
  color?: IconColor;
  filled?: boolean;
  weight?: 200 | 300 | 400 | 500;
  className?: string;
  disabled?: boolean;
  backgroundColor?: IconColor; // Optional background color for the icon container
}

export const Icon = ({
  name,
  size,
  filled = false,
  weight = 300,
  color,
  className = '',
  disabled = false,
  backgroundColor,
  ...restProps
}: IconProps) => {
  // TODO: Dark theme support - migrate from hard-coded SCSS variables to CSS custom properties
  // that respond to [data-mui-color-scheme="dark"] attribute to ensure icon colors adapt when
  // dark mode is implemented. This approach gives us more control over color values.
  let resolvedColor = color;
  if (color === 'error') {
    resolvedColor = scssVariables.ColorError80;
  } else if (color === 'success') {
    resolvedColor = scssVariables.ColorSuccess80;
  } else if (color === 'warning') {
    resolvedColor = scssVariables.ColorWarning80;
  } else if (color === 'info') {
    resolvedColor = scssVariables.ColorInfo80;
  } else if (color === 'secondary') {
    resolvedColor = scssVariables.ColorFog80;
  } else if (color === 'lightGray') {
    resolvedColor = scssVariables.ColorBlack20;
  } else if (backgroundColor) {
    resolvedColor = scssVariables.ColorBlack00;
  }

  let resolvedBackgroundColor = backgroundColor;
  if (backgroundColor === 'error') {
    resolvedBackgroundColor = scssVariables.ColorError100;
  } else if (backgroundColor === 'success') {
    resolvedBackgroundColor = scssVariables.ColorSuccess80;
  } else if (backgroundColor === 'warning') {
    resolvedBackgroundColor = scssVariables.ColorWarning100;
  } else if (backgroundColor === 'info') {
    resolvedBackgroundColor = scssVariables.ColorInfo80;
  } else if (backgroundColor === 'secondary') {
    resolvedBackgroundColor = scssVariables.ColorFog80;
  } else if (backgroundColor === 'lightGray') {
    resolvedBackgroundColor = scssVariables.ColorFog20;
  }

  // Only set inline styles if props are explicitly provided
  const inlineStyles: Record<string, string> = {};

  if (size !== undefined) {
    const numericSize =
      typeof size === 'number'
        ? size
        : ({
            micro: 16,
            tiny: 18,
            small: 20,
            medium: 24,
            large: 32,
            xLarge: 48,
            xxLarge: 64,
            xxxLarge: 96,
          }[size] ?? 20);
    inlineStyles['--icon-size'] = `${numericSize}px`;
    inlineStyles['--icon-variation-settings'] =
      `'FILL' ${filled ? 1 : 0}, 'wght' ${weight}, 'GRAD' 0, 'opsz' ${numericSize}`;
  } else if (filled || weight !== 300) {
    // If size is not provided but filled/weight are, use default size
    inlineStyles['--icon-variation-settings'] =
      `'FILL' ${filled ? 1 : 0}, 'wght' ${weight}, 'GRAD' 0, 'opsz' 20`;
  }

  if (resolvedColor !== undefined) {
    inlineStyles['--icon-color'] = resolvedColor;
  }

  if (resolvedBackgroundColor !== undefined) {
    inlineStyles['--icon-background-color'] = resolvedBackgroundColor;
  }

  return (
    <span
      className={cx(
        `${styles.Icon} icon material-symbols-rounded ${className}`,
        {
          [styles.Disabled]: disabled,
          [styles.HasBackground]: backgroundColor !== undefined,
        },
      )}
      style={
        Object.keys(inlineStyles).length > 0
          ? (inlineStyles as CSSProperties)
          : undefined
      }
      translate="no"
      aria-hidden="true"
      {...restProps}
    >
      {name.toLowerCase()}
    </span>
  );
};
